import React from "react";
import { InputComponent } from "../../core/Forms";
import { clearFieldError, handleErrors } from "../../../utils";
import { getMonthLabel } from "../../../utils/DateTimeRange";
import { Button } from "../Button";
import { validateBudgetForm } from "../../../modules/settings/settings";
import { initialBudgetState } from "../../../constants";
import { useBudgetStore } from "../../../state/useBudgetStore";
import { addCommasToNumber } from "../../../utils/convertToFloat";

interface BudgetModalProps {
  setShowModal: (showModal: boolean) => void;
}

const BudgetModal = ({ setShowModal }: BudgetModalProps) => {
  const { budgets, updateExistingBudget, createNewBudget } = useBudgetStore();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentMonthDropDown = [
    {
      value: `${currentMonth.toString().padStart(2, "0")}-${currentYear}`,
      label: getMonthLabel(currentMonth),
    },
  ];
  const [isLoading, setIsLoading] = React.useState(false);
  const [formData, setFormData] = React.useState({
    monthYear: currentMonthDropDown[0].value,
    allocatedBudget: budgets[0]?.allocatedBudget ?? 0,
  });
  const [validationResults, setValidationResults] = React.useState({
    monthYear: "",
    allocatedBudget: "",
  });
  React.useEffect(() => {
    if (budgets.length > 0) {
      setFormData({
        allocatedBudget: budgets[0]?.allocatedBudget,
        monthYear: budgets[0]?.monthYear,
      });
    } else {
      setFormData(initialBudgetState);
    }
  }, [budgets]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    clearFieldError(name, validationResults, setValidationResults);
  };
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const hasErrors = validateBudgetForm(
        {
          allocatedBudget: Number(formData.allocatedBudget),
          monthYear: currentMonthDropDown[0].value,
        },
        setValidationResults,
      );
      if (!hasErrors) {
        const data = {
          ...formData,
          allocatedBudget: Number(formData.allocatedBudget),
        };
        if (budgets[0]?.allocatedBudget || budgets[0]?.actualExpenses) {
          await updateExistingBudget(data);
        } else {
          await createNewBudget({
            allocatedBudget: Number(formData.allocatedBudget),
            monthYear: currentMonthDropDown[0].value,
          });
        }
        setShowModal(false);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  return (
    <div
      data-testid="budget-modal"
      className="bg-gray-900 bg-opacity-50 fixed top-0 right-0 left-0 z-50 h-[100vh]"
    >
      <div className="flex justify-center items-center h-full w-full">
        <div
          className={`fixed md:top-[25%] z-50 w-[91%] md:w-[482px] p-4 bg-white rounded-[8px]`}
        >
          <h1 className="text-[16px] md:text-[20px] font-semibold">
            Set Budget
          </h1>
          <p className="mt-2 mb-3 md:my-3 text-[14px] md:text-[16px]">
            Set a monthly budget for your expenses.
          </p>
          {/* <hr className="border border-[#BCBCBC] w-full" /> */}

          <div className="mt-4 space-y-4 md:mt-8 md:space-y-6">
            <div>
              <div className="text-[14px] md:text-[16px] mb-2">Amount</div>
              <InputComponent
                placeholder="Amount"
                name="allocatedBudget"
                pattern="[0-9]*"
                type="text"
                value={addCommasToNumber(formData.allocatedBudget ?? 0)}
                handleChange={handleInputChange}
                className="w-full"
                error={validationResults.allocatedBudget}
              />
            </div>
            <div className="w-full">
              <div className="text-[14px] md:text-[16px] text-black mb-2">
                Month
              </div>
              <InputComponent
                placeholder="Select Month"
                name="month"
                type="text"
                value={currentMonthDropDown[0].label}
                disabled
                className="w-full"
                error={validationResults.monthYear}
              />
            </div>
          </div>

          <div className="flex gap-4 mt-8">
            <Button
              onClick={() => setShowModal(false)}
              text="Cancel"
              size="full"
              font="semibold"
              variant="secondary"
              outline={true}
            />
            <Button
              text="Save Changes"
              size="full"
              font="semibold"
              variant="secondary"
              onClick={handleSubmit}
              disabled={isLoading}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetModal;
